import { PLATFORM_BASE_URL } from "../platform/config";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import React from "react";
import {
  OktonDataGrid,
  OktonDataGridColumn,
  OktonMetricCard,
  OktonPageHeader,
  useOktonAuth,
  OktonLoadingState,
  OktonErrorState,
  OktonInput,
  OktonSelect,
  OktonTextarea,
} from "@okton/platform-ui";

const STATUS_LABELS: Record<number, string> = {
  1: "Ativo",
  2: "Inativo",
  3: "Bloqueado",
  4: "Aguardando Instalação",
  5: "Aguardando Treinamento",
  6: "Cancelado",
  7: "Aguardando Agendamento",
  8: "Bloqueado/Pagamento",
  9: "Aguardando Contrato",
};

function statusColor(status?: number): string {
  switch (Number(status)) {
    case 1:
      return "green";
    case 2:
    case 6:
    case 8:
      return "red";
    case 3:
      return "blue";
    case 4:
      return "purple";
    case 5:
      return "orange";
    case 7:
      return "pink";
    case 9:
      return "yellow";
    default:
      return "gray";
  }
}

interface CompanySummary {
  code: number;
  legalName: string;
  active: boolean;
  branches: number;
  activePlans: number;
  mainBranch?: string;
  document?: string;
  branchList?: Array<{
    code: number;
    tradeName?: string;
    legalName?: string;
    document?: string;
    status?: number;
    effectiveStatus?: number;
    dueDay?: number;
    activePlans?: number;
  }>;
}

interface CompanyDetail {
  company: Record<string, any>;
  branches: Array<Record<string, any>>;
  plans: Array<Record<string, any>>;
}

interface CompanyGridRow {
  id: string;
  kind: "company" | "branch";
  companyCode: number;
  branchCode?: number;
  codeLabel: string;
  name: string;
  secondary?: string;
  document?: string;
  activePlans: number;
  statusLabel: string;
  statusColor: string;
}

export const CompaniesPage: React.FC = () => {
  const { accessToken } = useOktonAuth();
  const [rows, setRows] = React.useState<CompanySummary[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [pages, setPages] = React.useState(1);
  const [query, setQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("-1");
  const [summary, setSummary] = React.useState<{
    total: number;
    activeRegistered?: number;
    cancelledRegistered?: number;
    statuses: Record<string, number>;
  }>({
    total: 0,
    activeRegistered: 0,
    cancelledRegistered: 0,
    statuses: {},
  });
  const [showSecondaryMetrics, setShowSecondaryMetrics] = React.useState(false);
  const [detail, setDetail] = React.useState<CompanyDetail | null>(null);
  const [selectedBranch, setSelectedBranch] = React.useState<Record<
    string,
    any
  > | null>(null);
  const [contractPackages, setContractPackages] = React.useState<
    Array<Record<string, any>>
  >([]);
  const [contractPlans, setContractPlans] = React.useState<
    Array<Record<string, any>>
  >([]);
  const [contractPackageId, setContractPackageId] = React.useState("");
  const [contractSelectedCodes, setContractSelectedCodes] = React.useState<
    number[]
  >([]);
  const [contractSearch, setContractSearch] = React.useState("");
  const [selectedBranchPlan, setSelectedBranchPlan] = React.useState<Record<
    string,
    any
  > | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [importing, setImporting] = React.useState(false);
  const modal = useDisclosure();
  const branchModal = useDisclosure();
  const contractModal = useDisclosure();
  const branchPlanModal = useDisclosure();
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const authHeaders = React.useMemo(
    () => ({
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    }),
    [accessToken],
  );

  const load = React.useCallback(
    async (
      autoImport = true,
      targetPage = page,
      targetQuery = query,
      targetStatus = statusFilter,
    ) => {
      if (!accessToken) return;
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          page: String(targetPage),
          limit: "50",
        });
        if (targetQuery.trim()) params.set("q", targetQuery.trim());
        if (targetStatus !== "-1") params.set("status", targetStatus);

        const response = await fetch(
          `${PLATFORM_BASE_URL}/v1/admin/companies?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            credentials: "include",
          },
        );
        const payload = await response.json();
        if (!response.ok)
          throw new Error(
            payload.message || "Não foi possível carregar as empresas.",
          );

        const data = payload.data || {};
        setRows(data.rows || []);
        setTotal(Number(data.total || 0));
        setPage(Number(data.page || 1));
        setPages(Number(data.pages || 1));
        setSummary(data.summary || { total: 0, statuses: {} });

        if (autoImport && Number(data.summary?.total || 0) === 0) {
          setImporting(true);
          const importResponse = await fetch(
            `${PLATFORM_BASE_URL}/v1/admin/companies/import-iflex`,
            {
              method: "POST",
              headers: authHeaders,
              credentials: "include",
            },
          );
          const importPayload = await importResponse.json();
          if (!importResponse.ok)
            throw new Error(
              importPayload.message ||
                "Não foi possível importar o cadastro do iFlex.",
            );
          setImporting(false);
          await load(false, 1, targetQuery, targetStatus);
          return;
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Erro ao carregar empresas.",
        );
        setImporting(false);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, authHeaders, page, query, statusFilter],
  );

  React.useEffect(() => {
    load();
  }, [accessToken]);

  async function importFromIflex(): Promise<void> {
    if (!accessToken) return;
    setImporting(true);
    setError("");
    try {
      const response = await fetch(
        `${PLATFORM_BASE_URL}/v1/admin/companies/import-iflex`,
        {
          method: "POST",
          headers: authHeaders,
          credentials: "include",
        },
      );
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(
          payload.message || "Não foi possível atualizar os dados do iFlex.",
        );
      }
      await load(false, page, query, statusFilter);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Erro ao atualizar dados do iFlex.",
      );
    } finally {
      setImporting(false);
    }
  }

  async function fetchCompany(code: number): Promise<CompanyDetail | null> {
    if (!accessToken) return null;
    setError("");
    const response = await fetch(
      `${PLATFORM_BASE_URL}/v1/admin/companies/${code}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: "include",
      },
    );
    const payload = await response.json();
    if (!response.ok) {
      setError(payload.message || "Não foi possível carregar a empresa.");
      return null;
    }
    return payload.data;
  }

  async function openCompany(code: number): Promise<void> {
    const data = await fetchCompany(code);
    if (!data) return;
    setDetail(data);
    modal.onOpen();
  }

  async function openBranchQuick(
    companyCode: number,
    branchCode: number,
  ): Promise<void> {
    const data = await fetchCompany(companyCode);
    if (!data) return;
    const branch = data.branches.find(
      (item) => Number(item.code) === Number(branchCode),
    );
    if (!branch) {
      setError("Filial não encontrada.");
      return;
    }
    setDetail(data);
    setSelectedBranch({ ...branch });
    branchModal.onOpen();
  }

  async function createCompany(): Promise<void> {
    setDetail({
      company: {
        legal_name: "",
        active: true,
        online: true,
        decimal_places: 2,
        quantity_decimal_places: 2,
        share_customers: true,
        share_taxation: true,
        share_departments: true,
        share_service_types: true,
        multiple_databases: false,
      },
      branches: [],
      plans: [],
    });
    modal.onOpen();
  }

  async function saveCompany(): Promise<void> {
    if (!accessToken || !detail) return;
    setSaving(true);
    setError("");
    try {
      const code = detail.company.code;
      const url = code
        ? `${PLATFORM_BASE_URL}/v1/admin/companies/${code}`
        : `${PLATFORM_BASE_URL}/v1/admin/companies`;
      const response = await fetch(url, {
        method: code ? "PUT" : "POST",
        headers: authHeaders,
        credentials: "include",
        body: JSON.stringify(detail.company),
      });
      const payload = await response.json();
      if (!response.ok)
        throw new Error(
          payload.message || "Não foi possível salvar a empresa.",
        );
      setDetail(payload.data);
      await load(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar empresa.");
    } finally {
      setSaving(false);
    }
  }

  function openBranch(branch?: Record<string, any>): void {
    setSelectedBranch(
      branch
        ? { ...branch }
        : {
            status: 1,
            trade_name: "",
            legal_name: detail?.company.legal_name || "",
            person_type: 2,
            country_code: 1058,
            generate_billing: true,
            implementation_installments: 1,
            contract_sent: 0,
            issuer_type: 1,
          },
    );
    branchModal.onOpen();
  }

  async function saveBranch(): Promise<void> {
    if (!accessToken || !detail || !selectedBranch || !detail.company.code)
      return;
    setSaving(true);
    setError("");
    try {
      const branchCode = selectedBranch.code;
      const url = branchCode
        ? `${PLATFORM_BASE_URL}/v1/admin/companies/${detail.company.code}/branches/${branchCode}`
        : `${PLATFORM_BASE_URL}/v1/admin/companies/${detail.company.code}/branches`;
      const response = await fetch(url, {
        method: branchCode ? "PUT" : "POST",
        headers: authHeaders,
        credentials: "include",
        body: JSON.stringify(selectedBranch),
      });
      const payload = await response.json();
      if (!response.ok)
        throw new Error(payload.message || "Não foi possível salvar a filial.");
      setDetail(payload.data);
      branchModal.onClose();
      await load(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar filial.");
    } finally {
      setSaving(false);
    }
  }

  async function openContract(): Promise<void> {
    if (!accessToken || !detail || !selectedBranch?.code) return;
    setSaving(true);
    setError("");
    try {
      const [packagesResponse, plansResponse] = await Promise.all([
        fetch(`${PLATFORM_BASE_URL}/v1/admin/commercial/packages`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        }),
        fetch(`${PLATFORM_BASE_URL}/v1/admin/catalog/plans`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        }),
      ]);
      const packagesPayload = await packagesResponse.json();
      const plansPayload = await plansResponse.json();
      if (!packagesResponse.ok)
        throw new Error(
          packagesPayload.message || "Não foi possível carregar os pacotes.",
        );
      if (!plansResponse.ok)
        throw new Error(
          plansPayload.message || "Não foi possível carregar os planos.",
        );

      setContractPackages(
        (packagesPayload.data || []).filter((item: any) => item.active),
      );
      setContractPlans(
        (plansPayload.data || []).filter((item: any) => item.active),
      );
      setContractPackageId("");
      setContractSelectedCodes([]);
      setContractSearch("");
      contractModal.onOpen();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao preparar contratação.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function selectContractPackage(packageId: string): Promise<void> {
    setContractPackageId(packageId);
    if (!accessToken || !packageId) {
      setContractSelectedCodes([]);
      return;
    }

    const response = await fetch(
      `${PLATFORM_BASE_URL}/v1/admin/commercial/packages/${packageId}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: "include",
      },
    );
    const payload = await response.json();
    if (!response.ok) {
      setError(payload.message || "Não foi possível carregar o pacote.");
      return;
    }

    setContractSelectedCodes(
      (payload.data.plans || []).map((plan: any) => Number(plan.code)),
    );
  }

  function toggleContractPlan(code: number, checked: boolean): void {
    setContractSelectedCodes((current) => {
      if (checked) return Array.from(new Set([...current, code]));
      return current.filter((item) => item !== code);
    });
  }

  async function saveContract(): Promise<void> {
    if (!accessToken || !detail || !selectedBranch?.code) return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${PLATFORM_BASE_URL}/v1/admin/companies/${detail.company.code}/branches/${selectedBranch.code}/plans`,
        {
          method: "POST",
          headers: authHeaders,
          credentials: "include",
          body: JSON.stringify({
            planCodes: contractSelectedCodes,
            packageOriginId: contractPackageId
              ? Number(contractPackageId)
              : null,
          }),
        },
      );
      const payload = await response.json();
      if (!response.ok)
        throw new Error(
          payload.message || "Não foi possível adicionar os planos.",
        );

      setDetail(payload.data.company);
      contractModal.onClose();
      await load(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao adicionar contratação.",
      );
    } finally {
      setSaving(false);
    }
  }

  function openBranchPlan(plan: Record<string, any>): void {
    setSelectedBranchPlan({
      ...plan,
      start_at: plan.start_at ? String(plan.start_at).slice(0, 10) : "",
      trial_ends_at: plan.trial_ends_at
        ? String(plan.trial_ends_at).slice(0, 10)
        : "",
      period_discount_until: plan.period_discount_until
        ? String(plan.period_discount_until).slice(0, 10)
        : "",
      deactivated_at: plan.deactivated_at
        ? String(plan.deactivated_at).slice(0, 10)
        : "",
    });
    branchPlanModal.onOpen();
  }

  function patchBranchPlan(key: string, value: any): void {
    setSelectedBranchPlan((current) =>
      current ? { ...current, [key]: value } : current,
    );
  }

  function previewBranchPlan(plan: Record<string, any> | null): {
    suggested: number;
    discount: number;
    monthly: number;
    maxDiscount: number;
  } {
    if (!plan) return { suggested: 0, discount: 0, monthly: 0, maxDiscount: 0 };

    const monthlyAmount = Number(plan.monthly_amount || 0);
    const installations = Number(plan.included_installations || 0);
    const packages = Number(plan.included_packages || 0);
    let suggested = monthlyAmount;
    let maxDiscount = Number(plan.max_monthly_discount || 0);

    if (plan.billing_type === "IN") {
      suggested = installations * monthlyAmount;
      maxDiscount *= installations;
    } else if (plan.billing_type === "PC") {
      suggested = packages * monthlyAmount;
      maxDiscount *= packages;
    }

    let discount = Number(plan.discount_amount || 0);
    const billingDate = String(plan.billing_date || "").slice(0, 10);
    if (
      Boolean(plan.period_discount) &&
      plan.period_discount_until &&
      (!billingDate ||
        String(plan.period_discount_until).slice(0, 10) >= billingDate)
    ) {
      discount += Number(plan.period_discount_amount || 0);
    }

    let monthly = suggested + Number(plan.surcharge_amount || 0) - discount;
    if (Boolean(plan.courtesy)) monthly = 0;
    if (
      Boolean(plan.trial) &&
      plan.trial_ends_at &&
      (!billingDate || String(plan.trial_ends_at).slice(0, 10) >= billingDate)
    ) {
      monthly = 0;
    }

    return {
      suggested,
      discount,
      monthly: Math.max(0, monthly),
      maxDiscount,
    };
  }

  async function saveBranchPlan(): Promise<void> {
    if (
      !accessToken ||
      !detail ||
      !selectedBranch?.code ||
      !selectedBranchPlan?.id
    )
      return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(
        `${PLATFORM_BASE_URL}/v1/admin/companies/${detail.company.code}/branches/${selectedBranch.code}/plans/${selectedBranchPlan.id}`,
        {
          method: "PUT",
          headers: authHeaders,
          credentials: "include",
          body: JSON.stringify(selectedBranchPlan),
        },
      );
      const payload = await response.json();
      if (!response.ok)
        throw new Error(
          payload.message || "Não foi possível salvar o plano contratado.",
        );

      setDetail(payload.data);
      branchPlanModal.onClose();
      await load(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao salvar plano contratado.",
      );
    } finally {
      setSaving(false);
    }
  }

  function patchCompany(key: string, value: any): void {
    setDetail((current) =>
      current
        ? {
            ...current,
            company: { ...current.company, [key]: value },
          }
        : current,
    );
  }

  function patchBranch(key: string, value: any): void {
    setSelectedBranch((current) =>
      current ? { ...current, [key]: value } : current,
    );
  }

  const statusTotals = summary.statuses || {};
  const pendingSetup =
    Number(statusTotals["4"] || 0) +
    Number(statusTotals["5"] || 0) +
    Number(statusTotals["7"] || 0) +
    Number(statusTotals["9"] || 0);

  function applyFilters(): void {
    load(false, 1, query, statusFilter);
  }

  function clearFilters(): void {
    setQuery("");
    setStatusFilter("-1");
    load(false, 1, "", "-1");
  }

  const companyGridRows: CompanyGridRow[] = rows.flatMap((row) => [
    {
      id: `company-${row.code}`,
      kind: "company",
      companyCode: row.code,
      codeLabel: String(row.code),
      name: row.legalName,
      secondary: `${row.branches} filial${row.branches === 1 ? "" : "is"}`,
      document: row.document,
      activePlans: row.activePlans,
      statusLabel: row.active ? "Ativa" : "Inativa",
      statusColor: row.active ? "green" : "gray",
    },
    ...(row.branchList || []).map((branch): CompanyGridRow => {
      const effectiveStatus = Number(
        branch.effectiveStatus || branch.status || 0,
      );
      return {
        id: `branch-${row.code}-${branch.code}`,
        kind: "branch",
        companyCode: row.code,
        branchCode: branch.code,
        codeLabel: `${row.code}.${branch.code}`,
        name: branch.tradeName || branch.legalName || `Filial ${branch.code}`,
        secondary: branch.legalName || "-",
        document: branch.document,
        activePlans: Number(branch.activePlans || 0),
        statusLabel: STATUS_LABELS[effectiveStatus] || "Sem status",
        statusColor: statusColor(effectiveStatus),
      };
    }),
  ]);

  const companyColumns: OktonDataGridColumn<CompanyGridRow>[] = [
    { key: "code", header: "Código", value: (row) => row.codeLabel },
    {
      key: "name",
      header: "Empresa / Filial",
      render: (row) => (
        <Box pl={row.kind === "branch" ? "6" : "0"}>
          <Text fontWeight={row.kind === "company" ? "700" : "600"}>
            {row.kind === "branch" ? "↳ " : ""}
            {row.name}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {row.secondary || "-"}
          </Text>
        </Box>
      ),
    },
    {
      key: "document",
      header: "Documento",
      value: (row) => row.document || "-",
    },
    { key: "plans", header: "Planos ativos", value: (row) => row.activePlans },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <Badge colorScheme={row.statusColor}>{row.statusLabel}</Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (row) =>
        row.kind === "company" ? (
          <Button
            size="xs"
            variant="outline"
            onClick={() => openCompany(row.companyCode)}
          >
            Empresa
          </Button>
        ) : (
          <Button
            size="xs"
            colorScheme="blue"
            onClick={() =>
              openBranchQuick(row.companyCode, row.branchCode || 0)
            }
          >
            Abrir filial
          </Button>
        ),
    },
  ];

  const branchColumns: OktonDataGridColumn<Record<string, any>>[] = [
    { key: "code", header: "Código", value: (branch) => branch.code },
    {
      key: "tradeName",
      header: "Nome fantasia",
      render: (branch) => (
        <Text fontWeight="600">{branch.trade_name || "-"}</Text>
      ),
    },
    {
      key: "legalName",
      header: "Razão social",
      value: (branch) => branch.legal_name || "-",
    },
    {
      key: "document",
      header: "CNPJ/CPF",
      value: (branch) => branch.cnpj || branch.cpf || "-",
    },
    {
      key: "dueDay",
      header: "Vencimento",
      value: (branch) => branch.due_day || "-",
    },
    {
      key: "status",
      header: "Status",
      value: (branch) => branch.status ?? "-",
    },
    {
      key: "actions",
      header: "",
      render: (branch) => (
        <Button size="xs" onClick={() => openBranch(branch)}>
          Editar
        </Button>
      ),
    },
  ];

  const branchPlanColumns: OktonDataGridColumn<Record<string, any>>[] = [
    { key: "code", header: "Código", value: (plan) => plan.plan_code },
    {
      key: "name",
      header: "Plano",
      render: (plan) => <Text fontWeight="600">{plan.plan_name}</Text>,
    },
    {
      key: "billing",
      header: "Cobrança",
      value: (plan) => plan.billing_type,
    },
    {
      key: "installations",
      header: "Instalações",
      value: (plan) => plan.included_installations,
    },
    {
      key: "packages",
      header: "Pacotes",
      value: (plan) => plan.included_packages,
    },
    {
      key: "suggested",
      header: "Valor sugerido",
      value: (plan) =>
        Number(plan.suggested_amount || 0).toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        }),
    },
    {
      key: "monthly",
      header: "Valor mensal",
      render: (plan) => (
        <Text fontWeight="600">
          {Number(plan.calculated_monthly || 0).toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          })}
        </Text>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (plan) => (
        <Button size="xs" onClick={() => openBranchPlan(plan)}>
          Editar
        </Button>
      ),
    },
  ];

  return (
    <Box px={[4, 6, 9]} py={[6, 8, 16]} w="100%">
      <OktonPageHeader
        title="Empresas"
        subtitle="Cadastro central de empresas, filiais e planos contratados."
        actions={
          <Flex gap="3" wrap="wrap">
            <Button
              variant="outline"
              onClick={importFromIflex}
              isLoading={importing}
            >
              Atualizar do iFlex
            </Button>
            <Button colorScheme="blue" onClick={createCompany}>
              Nova empresa
            </Button>
          </Flex>
        }
      />

      <SimpleGrid columns={[1, 2, 4]} spacing="4" mb="3">
        <OktonMetricCard
          label="Ativas"
          value={Number(summary.activeRegistered || 0)}
        />
        <OktonMetricCard
          label="Bloq./Pagamento"
          value={Number(statusTotals["8"] || 0)}
        />
        <OktonMetricCard label="Em implantação" value={pendingSetup} />
        <OktonMetricCard
          label="Inativas/Bloqueadas"
          value={
            Number(statusTotals["2"] || 0) + Number(statusTotals["3"] || 0)
          }
        />
      </SimpleGrid>

      <Flex justify="flex-end" mb="4">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setShowSecondaryMetrics((value) => !value)}
        >
          {showSecondaryMetrics
            ? "Ocultar outros indicadores"
            : "Outros indicadores"}
        </Button>
      </Flex>

      {showSecondaryMetrics && (
        <SimpleGrid columns={[1, 2, 3, 6]} spacing="3" mb="6">
          <OktonMetricCard
            label="Total de cadastros"
            value={Number(summary.total || 0)}
          />
          <OktonMetricCard
            label="Canceladas"
            value={Number(summary.cancelledRegistered || 0)}
          />
          <OktonMetricCard
            label="Aguard. Instalação"
            value={Number(statusTotals["4"] || 0)}
          />
          <OktonMetricCard
            label="Aguard. Treinamento"
            value={Number(statusTotals["5"] || 0)}
          />
          <OktonMetricCard
            label="Aguard. Agendamento"
            value={Number(statusTotals["7"] || 0)}
          />
          <OktonMetricCard
            label="Aguard. Contrato"
            value={Number(statusTotals["9"] || 0)}
          />
        </SimpleGrid>
      )}

      <Flex
        gap="3"
        mb="4"
        direction={["column", "column", "row"]}
        align={["stretch", "stretch", "flex-end"]}
      >
        <Box maxW={["100%", "100%", "430px"]} w="100%">
          <OktonInput
            name="company-search"
            label="Buscar"
            value={query}
            placeholder="Empresa, filial, CNPJ, CPF ou telefone"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilters();
            }}
          />
        </Box>
        <Box maxW={["100%", "100%", "260px"]} w="100%">
          <OktonSelect
            name="company-status"
            label="Status da filial"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="-1">Todos</option>
            {Object.entries(STATUS_LABELS).map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </OktonSelect>
        </Box>
        <Button colorScheme="blue" onClick={applyFilters}>
          Filtrar
        </Button>
        <Button variant="outline" onClick={clearFilters}>
          Limpar
        </Button>
      </Flex>

      <Flex justify="space-between" align="center" mb="3">
        <Text fontSize="sm" color="gray.500">
          {total} empresa{total === 1 ? "" : "s"} encontrada
          {total === 1 ? "" : "s"}
        </Text>
        <Text fontSize="sm" color="gray.500">
          Página {page} de {pages}
        </Text>
      </Flex>

      {loading ? (
        <OktonLoadingState
          title={
            importing
              ? "Importando cadastro do iFlex..."
              : "Carregando empresas..."
          }
          minH="180px"
        />
      ) : error ? (
        <OktonErrorState
          title="Não foi possível carregar as empresas"
          description={error}
          minH="180px"
        />
      ) : (
        <OktonDataGrid
          rows={companyGridRows}
          columns={companyColumns}
          getRowId={(row) => row.id}
          emptyMessage="Nenhuma empresa cadastrada. Cadastre uma empresa ou atualize os dados do iFlex."
          hidePagination
        />
      )}

      <Flex justify="space-between" align="center" mt="4" gap="3">
        <Button
          variant="outline"
          isDisabled={page <= 1 || loading}
          onClick={() => load(false, page - 1, query, statusFilter)}
        >
          Anterior
        </Button>
        <Text fontSize="sm" color="gray.500">
          Página {page} de {pages}
        </Text>
        <Button
          variant="outline"
          isDisabled={page >= pages || loading}
          onClick={() => load(false, page + 1, query, statusFilter)}
        >
          Próxima
        </Button>
      </Flex>

      <Modal
        isOpen={modal.isOpen}
        onClose={modal.onClose}
        size="6xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {detail?.company?.code
              ? `Empresa ${detail.company.code} - ${detail.company.legal_name}`
              : "Nova empresa"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {error && (
              <Text color="red.500" mb="4">
                {error}
              </Text>
            )}
            {detail && (
              <Tabs colorScheme="blue">
                <TabList>
                  <Tab>Empresa</Tab>
                  <Tab>Filiais</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px="0">
                    <SimpleGrid columns={[1, 2]} spacing="4">
                      <Field
                        label="Razão social"
                        value={detail.company.legal_name}
                        onChange={(v) => patchCompany("legal_name", v)}
                      />
                      <Field
                        label="Código do banco"
                        value={detail.company.database_id}
                        onChange={(v) => patchCompany("database_id", v)}
                        type="number"
                      />
                      <Field
                        label="Casas decimais - valor"
                        value={detail.company.decimal_places}
                        onChange={(v) => patchCompany("decimal_places", v)}
                        type="number"
                      />
                      <Field
                        label="Casas decimais - quantidade"
                        value={detail.company.quantity_decimal_places}
                        onChange={(v) =>
                          patchCompany("quantity_decimal_places", v)
                        }
                        type="number"
                      />
                      <Field
                        label="Natureza jurídica"
                        value={detail.company.legal_nature}
                        onChange={(v) => patchCompany("legal_nature", v)}
                      />
                      <Field
                        label="Empresa superior/origem"
                        value={detail.company.parent_company_code}
                        onChange={(v) => patchCompany("parent_company_code", v)}
                        type="number"
                      />
                    </SimpleGrid>

                    <SimpleGrid columns={[1, 2, 3]} spacing="4" mt="5">
                      <Check
                        label="Acesso online"
                        checked={detail.company.online}
                        onChange={(v) => patchCompany("online", v)}
                      />
                      <Check
                        label="Compartilhar clientes"
                        checked={detail.company.share_customers}
                        onChange={(v) => patchCompany("share_customers", v)}
                      />
                      <Check
                        label="Compartilhar tributação"
                        checked={detail.company.share_taxation}
                        onChange={(v) => patchCompany("share_taxation", v)}
                      />
                      <Check
                        label="Compartilhar departamentos"
                        checked={detail.company.share_departments}
                        onChange={(v) => patchCompany("share_departments", v)}
                      />
                      <Check
                        label="Compartilhar tipos de atendimento"
                        checked={detail.company.share_service_types}
                        onChange={(v) => patchCompany("share_service_types", v)}
                      />
                      <Check
                        label="Várias bases"
                        checked={detail.company.multiple_databases}
                        onChange={(v) => patchCompany("multiple_databases", v)}
                      />
                      <Check
                        label="Empresa ativa"
                        checked={detail.company.active !== false}
                        onChange={(v) => patchCompany("active", v)}
                      />
                    </SimpleGrid>

                    <Flex justify="flex-end" mt="6">
                      <Button
                        colorScheme="blue"
                        isLoading={saving}
                        onClick={saveCompany}
                      >
                        Salvar empresa
                      </Button>
                    </Flex>
                  </TabPanel>

                  <TabPanel px="0">
                    <Flex justify="flex-end" mb="4">
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={() => openBranch()}
                      >
                        Nova filial
                      </Button>
                    </Flex>
                    <OktonDataGrid
                      rows={detail.branches}
                      columns={branchColumns}
                      getRowId={(branch) => branch.code}
                      emptyMessage="Nenhuma filial cadastrada."
                      hidePagination
                    />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={modal.onClose}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={branchModal.isOpen}
        onClose={branchModal.onClose}
        size="5xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedBranch?.code
              ? `Editar filial ${selectedBranch.code}`
              : "Nova filial"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedBranch && (
              <>
                <Heading size="sm" mb="3">
                  Dados principais
                </Heading>
                <SimpleGrid columns={[1, 2, 3]} spacing="4">
                  <Field
                    label="Nome fantasia"
                    value={selectedBranch.trade_name}
                    onChange={(v) => patchBranch("trade_name", v)}
                  />
                  <Field
                    label="Razão social"
                    value={selectedBranch.legal_name}
                    onChange={(v) => patchBranch("legal_name", v)}
                  />
                  <OktonSelect
                    name="branch-person-type"
                    label="Tipo de pessoa"
                    value={selectedBranch.person_type || 2}
                    onChange={(e) =>
                      patchBranch("person_type", Number(e.target.value))
                    }
                  >
                    <option value={1}>Pessoa Física</option>
                    <option value={2}>Pessoa Jurídica</option>
                  </OktonSelect>
                  <Field
                    label="CNPJ"
                    value={selectedBranch.cnpj}
                    onChange={(v) => patchBranch("cnpj", v)}
                  />
                  <Field
                    label="CPF"
                    value={selectedBranch.cpf}
                    onChange={(v) => patchBranch("cpf", v)}
                  />
                  <Field
                    label="E-mail"
                    value={selectedBranch.email}
                    onChange={(v) => patchBranch("email", v)}
                    type="email"
                  />
                  <Field
                    label="Telefone"
                    value={selectedBranch.phone}
                    onChange={(v) => patchBranch("phone", v)}
                  />
                  <Field
                    label="Telefone 2"
                    value={selectedBranch.phone2}
                    onChange={(v) => patchBranch("phone2", v)}
                  />
                  <Field
                    label="Telefone 3"
                    value={selectedBranch.phone3}
                    onChange={(v) => patchBranch("phone3", v)}
                  />
                </SimpleGrid>

                <Heading size="sm" mt="6" mb="3">
                  Endereço
                </Heading>
                <SimpleGrid columns={[1, 2, 3]} spacing="4">
                  <Field
                    label="CEP"
                    value={selectedBranch.postal_code}
                    onChange={(v) => patchBranch("postal_code", v)}
                  />
                  <Field
                    label="Rua"
                    value={selectedBranch.street}
                    onChange={(v) => patchBranch("street", v)}
                  />
                  <Field
                    label="Número"
                    value={selectedBranch.street_number}
                    onChange={(v) => patchBranch("street_number", v)}
                  />
                  <Field
                    label="Complemento"
                    value={selectedBranch.complement}
                    onChange={(v) => patchBranch("complement", v)}
                  />
                  <Field
                    label="Bairro"
                    value={selectedBranch.district}
                    onChange={(v) => patchBranch("district", v)}
                  />
                  <Field
                    label="Código município"
                    value={selectedBranch.city_code}
                    onChange={(v) => patchBranch("city_code", v)}
                    type="number"
                  />
                  <Field
                    label="Código país"
                    value={selectedBranch.country_code}
                    onChange={(v) => patchBranch("country_code", v)}
                    type="number"
                  />
                </SimpleGrid>

                <Accordion allowMultiple mt="6">
                  <AccordionItem>
                    <h2>
                      <AccordionButton>
                        <Box flex="1" textAlign="left" fontWeight="600">
                          Fiscal e enquadramento
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel px="0">
                      <SimpleGrid columns={[1, 2, 3]} spacing="4">
                        <Field
                          label="Inscrição Estadual"
                          value={selectedBranch.state_registration}
                          onChange={(v) => patchBranch("state_registration", v)}
                        />
                        <Field
                          label="Inscrição Municipal"
                          value={selectedBranch.municipal_registration}
                          onChange={(v) =>
                            patchBranch("municipal_registration", v)
                          }
                        />
                        <Field
                          label="SUFRAMA"
                          value={selectedBranch.suframa}
                          onChange={(v) => patchBranch("suframa", v)}
                        />
                        <Field
                          label="Ramo de atividade"
                          value={selectedBranch.business_activity_code}
                          onChange={(v) =>
                            patchBranch("business_activity_code", v)
                          }
                          type="number"
                        />
                        <Field
                          label="Ramo preponderante"
                          value={selectedBranch.predominant_activity_code}
                          onChange={(v) =>
                            patchBranch("predominant_activity_code", v)
                          }
                          type="number"
                        />
                        <Field
                          label="Tipo de enquadramento"
                          value={selectedBranch.tax_framework_type}
                          onChange={(v) => patchBranch("tax_framework_type", v)}
                          type="number"
                        />
                        <Field
                          label="Perfil"
                          value={selectedBranch.profile_code}
                          onChange={(v) => patchBranch("profile_code", v)}
                        />
                        <Field
                          label="Emitente"
                          value={selectedBranch.issuer_type}
                          onChange={(v) => patchBranch("issuer_type", v)}
                          type="number"
                        />
                        <Field
                          label="Regime de apuração"
                          value={selectedBranch.assessment_regime}
                          onChange={(v) => patchBranch("assessment_regime", v)}
                        />
                        <Field
                          label="Código tributação serviço"
                          value={selectedBranch.service_tax_code}
                          onChange={(v) => patchBranch("service_tax_code", v)}
                        />
                        <Field
                          label="Código tributação município"
                          value={selectedBranch.municipal_tax_code}
                          onChange={(v) => patchBranch("municipal_tax_code", v)}
                        />
                        <Field
                          label="Código contador"
                          value={selectedBranch.accountant_code}
                          onChange={(v) => patchBranch("accountant_code", v)}
                          type="number"
                        />
                      </SimpleGrid>
                    </AccordionPanel>
                  </AccordionItem>

                  <AccordionItem>
                    <h2>
                      <AccordionButton>
                        <Box flex="1" textAlign="left" fontWeight="600">
                          Cobrança e implantação
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel px="0">
                      <SimpleGrid columns={[1, 2, 3]} spacing="4">
                        <Field
                          label="Dia de vencimento"
                          value={selectedBranch.due_day}
                          onChange={(v) => patchBranch("due_day", v)}
                          type="number"
                        />
                        <Field
                          label="Parcelas implantação"
                          value={selectedBranch.implementation_installments}
                          onChange={(v) =>
                            patchBranch("implementation_installments", v)
                          }
                          type="number"
                        />
                        <Field
                          label="Forma pagto. implantação"
                          value={selectedBranch.implementation_payment_method}
                          onChange={(v) =>
                            patchBranch("implementation_payment_method", v)
                          }
                        />
                        <Field
                          label="Valor implantação"
                          value={selectedBranch.implementation_amount}
                          onChange={(v) =>
                            patchBranch("implementation_amount", v)
                          }
                          type="number"
                        />
                        <Field
                          label="Contrato enviado"
                          value={selectedBranch.contract_sent}
                          onChange={(v) => patchBranch("contract_sent", v)}
                          type="number"
                        />
                        <Field
                          label="Código operador"
                          value={selectedBranch.operator_code}
                          onChange={(v) => patchBranch("operator_code", v)}
                          type="number"
                        />
                      </SimpleGrid>
                      <Flex gap="6" mt="4" wrap="wrap">
                        <Check
                          label="Gerar cobrança"
                          checked={selectedBranch.generate_billing}
                          onChange={(v) => patchBranch("generate_billing", v)}
                        />
                        <Check
                          label="Movidesk sincronizado"
                          checked={selectedBranch.movidesk_synced}
                          onChange={(v) => patchBranch("movidesk_synced", v)}
                        />
                        <Check
                          label="Empresa do exterior"
                          checked={selectedBranch.foreign_entity}
                          onChange={(v) => patchBranch("foreign_entity", v)}
                        />
                      </Flex>
                    </AccordionPanel>
                  </AccordionItem>

                  <AccordionItem>
                    <h2>
                      <AccordionButton>
                        <Box flex="1" textAlign="left" fontWeight="600">
                          Outros dados
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel px="0">
                      <SimpleGrid columns={[1, 2, 3]} spacing="4">
                        <Field
                          label="Status"
                          value={selectedBranch.status}
                          onChange={(v) => patchBranch("status", v)}
                          type="number"
                        />
                        <Field
                          label="ID estrangeiro"
                          value={selectedBranch.foreign_id}
                          onChange={(v) => patchBranch("foreign_id", v)}
                        />
                        <Field
                          label="Código indicação"
                          value={selectedBranch.referral_code}
                          onChange={(v) => patchBranch("referral_code", v)}
                          type="number"
                        />
                        <Field
                          label="Data da venda"
                          value={selectedBranch.sold_at}
                          onChange={(v) => patchBranch("sold_at", v)}
                          type="date"
                        />
                        <Field
                          label="Data cancelamento"
                          value={selectedBranch.cancelled_at}
                          onChange={(v) => patchBranch("cancelled_at", v)}
                          type="date"
                        />
                        <Field
                          label="Fax"
                          value={selectedBranch.fax}
                          onChange={(v) => patchBranch("fax", v)}
                        />
                      </SimpleGrid>
                      <Box mt="4">
                        <OktonTextarea
                          name="branch-history"
                          label="Histórico"
                          value={selectedBranch.history || ""}
                          onChange={(e) =>
                            patchBranch("history", e.target.value)
                          }
                        />
                      </Box>
                      <Box mt="4">
                        <OktonTextarea
                          name="branch-cancellation-reason"
                          label="Motivo de cancelamento"
                          value={selectedBranch.cancellation_reason || ""}
                          onChange={(e) =>
                            patchBranch("cancellation_reason", e.target.value)
                          }
                        />
                      </Box>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>

                <Flex
                  mt="7"
                  mb="3"
                  align="center"
                  justify="space-between"
                  gap="3"
                >
                  <Heading size="sm">Planos contratados desta filial</Heading>
                  <Button size="sm" colorScheme="blue" onClick={openContract}>
                    Adicionar contratação
                  </Button>
                </Flex>
                {(() => {
                  const branchPlans = (detail?.plans || []).filter(
                    (plan) =>
                      String(plan.branch_id) === String(selectedBranch.code) &&
                      Boolean(plan.active),
                  );

                  return (
                    <OktonDataGrid
                      rows={branchPlans}
                      columns={branchPlanColumns}
                      getRowId={(plan) => plan.id}
                      emptyMessage="Nenhum plano ativo vinculado a esta filial."
                      hidePagination
                    />
                  );
                })()}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="3" onClick={branchModal.onClose}>
              Cancelar
            </Button>
            <Button colorScheme="blue" isLoading={saving} onClick={saveBranch}>
              Salvar filial
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={contractModal.isOpen}
        onClose={contractModal.onClose}
        size="5xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Adicionar contratação -{" "}
            {selectedBranch?.trade_name ||
              selectedBranch?.legal_name ||
              "Filial"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={[1, 2]} spacing="4" mb="5">
              <OktonSelect
                name="contract-package"
                label="Pacote comercial"
                value={contractPackageId}
                onChange={(e) => selectContractPackage(e.target.value)}
              >
                <option value="">Montar pelos Planos</option>
                {contractPackages.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.group_name} - {pkg.name}
                  </option>
                ))}
              </OktonSelect>
              <OktonInput
                name="contract-plan-search"
                label="Buscar plano"
                value={contractSearch}
                placeholder="Código ou nome do plano"
                onChange={(e) => setContractSearch(e.target.value)}
              />
            </SimpleGrid>

            <Text fontSize="sm" color="gray.500" mb="3">
              O pacote apenas preenche a seleção. Você pode personalizar os
              Planos antes de confirmar.
            </Text>

            <Box
              borderWidth="1px"
              borderColor={borderColor}
              rounded="md"
              maxH="420px"
              overflowY="auto"
            >
              {(() => {
                const q = contractSearch.trim().toLowerCase();
                const currentCodes = new Set(
                  (detail?.plans || [])
                    .filter(
                      (plan) =>
                        String(plan.branch_id) ===
                          String(selectedBranch?.code) && Boolean(plan.active),
                    )
                    .map((plan) => Number(plan.plan_code)),
                );
                const visiblePlans = contractPlans.filter((plan) => {
                  if (!q) return true;
                  return (
                    String(plan.code).includes(q) ||
                    String(plan.name || "")
                      .toLowerCase()
                      .includes(q)
                  );
                });

                return (
                  <Stack spacing="0">
                    {visiblePlans.map((plan) => {
                      const code = Number(plan.code);
                      const alreadyActive = currentCodes.has(code);
                      return (
                        <Flex
                          key={code}
                          px="4"
                          py="3"
                          align="center"
                          justify="space-between"
                          borderBottomWidth="1px"
                          borderColor={borderColor}
                        >
                          <Checkbox
                            isChecked={
                              alreadyActive ||
                              contractSelectedCodes.includes(code)
                            }
                            isDisabled={alreadyActive}
                            onChange={(e) =>
                              toggleContractPlan(code, e.target.checked)
                            }
                          >
                            <Text fontWeight="600">
                              {code} - {plan.name}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              Cobrança: {plan.billing_type}
                            </Text>
                          </Checkbox>
                          {alreadyActive ? (
                            <Badge colorScheme="green">Já ativo</Badge>
                          ) : contractSelectedCodes.includes(code) ? (
                            <Badge colorScheme="blue">Adicionar</Badge>
                          ) : null}
                        </Flex>
                      );
                    })}
                  </Stack>
                );
              })()}
            </Box>

            <Text mt="3" fontSize="sm" color="gray.500">
              {contractSelectedCodes.length} plano
              {contractSelectedCodes.length === 1 ? "" : "s"} selecionado
              {contractSelectedCodes.length === 1 ? "" : "s"}.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="3" onClick={contractModal.onClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              isLoading={saving}
              isDisabled={contractSelectedCodes.length === 0}
              onClick={saveContract}
            >
              Adicionar planos
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={branchPlanModal.isOpen}
        onClose={branchPlanModal.onClose}
        size="5xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedBranchPlan
              ? `Plano ${selectedBranchPlan.plan_code} - ${selectedBranchPlan.plan_name}`
              : "Plano contratado"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedBranchPlan &&
              (() => {
                const preview = previewBranchPlan(selectedBranchPlan);
                const discountAboveLimit =
                  Number(selectedBranchPlan.discount_amount || 0) >
                  preview.maxDiscount;

                return (
                  <>
                    <SimpleGrid columns={[1, 2, 4]} spacing="4" mb="5">
                      <Box
                        borderWidth="1px"
                        borderColor={borderColor}
                        rounded="md"
                        p="4"
                      >
                        <Text fontSize="xs" color="gray.500">
                          Valor sugerido
                        </Text>
                        <Text fontSize="lg" fontWeight="700">
                          {preview.suggested.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </Text>
                      </Box>
                      <Box
                        borderWidth="1px"
                        borderColor={borderColor}
                        rounded="md"
                        p="4"
                      >
                        <Text fontSize="xs" color="gray.500">
                          Desconto aplicado
                        </Text>
                        <Text fontSize="lg" fontWeight="700">
                          {preview.discount.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </Text>
                      </Box>
                      <Box
                        borderWidth="1px"
                        borderColor={borderColor}
                        rounded="md"
                        p="4"
                      >
                        <Text fontSize="xs" color="gray.500">
                          Valor mensal
                        </Text>
                        <Text fontSize="lg" fontWeight="700">
                          {preview.monthly.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </Text>
                      </Box>
                      <Box
                        borderWidth="1px"
                        borderColor={borderColor}
                        rounded="md"
                        p="4"
                      >
                        <Text fontSize="xs" color="gray.500">
                          Limite de desconto
                        </Text>
                        <Text fontSize="lg" fontWeight="700">
                          {preview.maxDiscount.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    <Heading size="sm" mb="3">
                      Contratação
                    </Heading>
                    <SimpleGrid columns={[1, 2, 3]} spacing="4">
                      <OktonInput
                        name="branch-plan-billing-type"
                        label="Tipo de cobrança"
                        value={selectedBranchPlan.billing_type || ""}
                        isReadOnly
                      />
                      <Field
                        label="Nº de instalações"
                        type="number"
                        value={selectedBranchPlan.included_installations}
                        onChange={(v) =>
                          patchBranchPlan("included_installations", v)
                        }
                      />
                      <Field
                        label="Nº de pacotes"
                        type="number"
                        value={selectedBranchPlan.included_packages}
                        onChange={(v) =>
                          patchBranchPlan("included_packages", v)
                        }
                      />
                      <Field
                        label="Data de início"
                        type="date"
                        value={selectedBranchPlan.start_at}
                        onChange={(v) => patchBranchPlan("start_at", v)}
                      />
                      <Field
                        label="Desconto"
                        type="number"
                        value={selectedBranchPlan.discount_amount}
                        onChange={(v) => patchBranchPlan("discount_amount", v)}
                      />
                      <Field
                        label="Acréscimo"
                        type="number"
                        value={selectedBranchPlan.surcharge_amount}
                        onChange={(v) => patchBranchPlan("surcharge_amount", v)}
                      />
                    </SimpleGrid>

                    {discountAboveLimit && (
                      <Box mt="4">
                        <OktonTextarea
                          name="branch-plan-discount-note"
                          label="Observação do desconto acima do limite"
                          value={selectedBranchPlan.discount_note || ""}
                          onChange={(e) =>
                            patchBranchPlan("discount_note", e.target.value)
                          }
                        />
                      </Box>
                    )}

                    <Flex gap="6" mt="5" wrap="wrap">
                      <Checkbox
                        isChecked={Boolean(selectedBranchPlan.courtesy)}
                        onChange={(e) =>
                          patchBranchPlan("courtesy", e.target.checked)
                        }
                      >
                        Cortesia
                      </Checkbox>
                      <Checkbox
                        isChecked={Boolean(selectedBranchPlan.trial)}
                        onChange={(e) =>
                          patchBranchPlan("trial", e.target.checked)
                        }
                      >
                        Período de teste
                      </Checkbox>
                      <Checkbox
                        isChecked={Boolean(selectedBranchPlan.period_discount)}
                        onChange={(e) =>
                          patchBranchPlan("period_discount", e.target.checked)
                        }
                      >
                        Desconto por período
                      </Checkbox>
                      <Checkbox
                        isChecked={Boolean(selectedBranchPlan.active)}
                        onChange={(e) =>
                          patchBranchPlan("active", e.target.checked)
                        }
                      >
                        Plano ativo
                      </Checkbox>
                    </Flex>

                    <SimpleGrid columns={[1, 2, 3]} spacing="4" mt="4">
                      {Boolean(selectedBranchPlan.trial) && (
                        <Field
                          label="Término do teste"
                          type="date"
                          value={selectedBranchPlan.trial_ends_at}
                          onChange={(v) => patchBranchPlan("trial_ends_at", v)}
                        />
                      )}
                      {Boolean(selectedBranchPlan.period_discount) && (
                        <>
                          <Field
                            label="Data limite do desconto"
                            type="date"
                            value={selectedBranchPlan.period_discount_until}
                            onChange={(v) =>
                              patchBranchPlan("period_discount_until", v)
                            }
                          />
                          <Field
                            label="Desconto do período"
                            type="number"
                            value={selectedBranchPlan.period_discount_amount}
                            onChange={(v) =>
                              patchBranchPlan("period_discount_amount", v)
                            }
                          />
                        </>
                      )}
                    </SimpleGrid>

                    {!Boolean(selectedBranchPlan.active) && (
                      <Box mt="5">
                        <Heading size="sm" mb="3">
                          Inativação
                        </Heading>
                        <SimpleGrid columns={[1, 2]} spacing="4">
                          <Field
                            label="Data de inativação"
                            type="date"
                            value={selectedBranchPlan.deactivated_at}
                            onChange={(v) =>
                              patchBranchPlan("deactivated_at", v)
                            }
                          />
                          <OktonTextarea
                            name="branch-plan-deactivation-reason"
                            label="Motivo da inativação"
                            value={selectedBranchPlan.deactivation_reason || ""}
                            onChange={(e) =>
                              patchBranchPlan(
                                "deactivation_reason",
                                e.target.value,
                              )
                            }
                          />
                        </SimpleGrid>
                      </Box>
                    )}

                    <Text mt="5" fontSize="sm" color="gray.500">
                      Data-base da cobrança:{" "}
                      {selectedBranchPlan.billing_date || "-"}
                      {selectedBranchPlan.package_origin_name
                        ? ` • Origem comercial: ${selectedBranchPlan.package_origin_name}`
                        : ""}
                    </Text>
                    {selectedBranchPlan.billing_type === "IN" && (
                      <Text mt="2" fontSize="sm" color="orange.500">
                        Ao reduzir instalações, o Core valida os dispositivos
                        licenciados ativos dos módulos deste plano. Se houver
                        mais dispositivos em uso que a nova quantidade, a
                        redução será bloqueada.
                      </Text>
                    )}
                  </>
                );
              })()}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="3" onClick={branchPlanModal.onClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              isLoading={saving}
              onClick={saveBranchPlan}
            >
              Salvar plano contratado
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const Field: React.FC<{
  label: string;
  value: any;
  type?: string;
  onChange: (value: any) => void;
}> = ({ label, value, type = "text", onChange }) => (
  <OktonInput
    name={`company-field-${label}`}
    label={label}
    type={type}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
  />
);

const Check: React.FC<{
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <Checkbox
    isChecked={Boolean(checked)}
    onChange={(e) => onChange(e.target.checked)}
  >
    {label}
  </Checkbox>
);
